/**
 * Temporary wrapper for smooth trasition from
 * Apollo useQuery to
 * custom hook useQueryLight
 */
import { useCallback } from 'react';

import { type DocumentNode } from 'graphql';
import { type QueryHookOptions, useQuery } from 'react-apollo';

import {
	type FetchMoreArgs,
	type QueryOptions,
	useQueryLight,
	type UseQueryLightError,
} from '@atlassian/teams-client';

import { type ApolloError } from './types';

interface UseQueryWrapperResponse<TParams, TResult> {
	data?: TResult;
	loading: boolean;
	error?: ApolloError | UseQueryLightError;
	fetchMore: (args: FetchMoreArgs<TParams, TResult>) => void;
}

export const useQueryWrapper = <TParams, TResult>(
	query: DocumentNode,
	options: QueryHookOptions & QueryOptions<TParams>,
	enableTeamsGqlQueryMigration: boolean,
): UseQueryWrapperResponse<TParams, TResult> => {
	const { data, loading, error, fetchMore } = useQuery<TResult>(query, {
		...options,
		skip: options.skip || enableTeamsGqlQueryMigration,
	});

	const {
		data: dataNext,
		loading: loadingNext,
		error: errorNext,
		fetchMore: fetchMoreNext,
	} = useQueryLight<TParams, TResult>(query, {
		operationName: options.operationName,
		serviceUrl: options.serviceUrl,
		customHeaders: options.customHeaders || options.context?.headers,
		variables: options.variables,
		skip: options.skip || !enableTeamsGqlQueryMigration,
	});

	const transformedApolloFetchMore = useCallback(
		({ variables, handleDataMerge }: FetchMoreArgs<TParams, TResult>) => {
			fetchMore({
				variables,
				updateQuery: (prevResult, { fetchMoreResult }) => {
					return handleDataMerge(prevResult, fetchMoreResult) || prevResult;
				},
			});
		},
		[fetchMore],
	);

	if (enableTeamsGqlQueryMigration) {
		return {
			data: dataNext,
			loading: loadingNext,
			error: errorNext,
			fetchMore: fetchMoreNext,
		};
	}

	return { loading, error, data, fetchMore: transformedApolloFetchMore };
};
